import { useCallback, useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getIntegrationDetails,
  getRoute,
  getRouteDetails,
} from 'api/routes/routesApi';
import { queryKeys } from 'api/queryKeys';
import {
  Destination,
  Provider,
  Route,
  buildProvider,
  buildRoute,
} from 'components/common/ObjectTypes';
import { loadCurrentUserDetails } from 'components/organizations/loadCurrentUserDetails';
import { routeDisplayName, routeRecordId } from '../routeDisplayName';

// The Route as the page and its tabs share it: the server's copy, plus the
// drafts the Configurations tab edits (name, workspace, webhook settings) and
// the list of destinations the tabs read.
export type RouteRecord = ReturnType<typeof useRouteRecord>;

// The URL carries the provider INTEGRATION id, not the route record id: the
// integration id is what backend logs carry and what populates the source
// provider id in EarthRanger, so a portal link can be correlated with a log
// line on its own. Resolve the provider's connection first, then scope down
// to its own route record — the page's `route` object keeps the connection
// shape downstream code expects, but its name and destinations are THIS
// route's, not the union across every route the provider feeds.
const fetchRouteScoped = async (tkn: string, routeId: string) => {
  const conn = await getRouteDetails(tkn, routeId);
  if (!conn || !conn.owner?.id) return null;
  const recordId = routeRecordId(conn);
  // An integration with no route record has a Connection page but no Route
  // page, so the caller's 404 is the right answer. Returning the connection
  // unscoped is not an acceptable fallback: its `destinations` is the union
  // across ALL of the provider's routes, and the PATCHes on this page
  // target ONE record — presenting the union would let a save graft
  // another route's destinations on. Better to fail the load than to show
  // editable data that lies. A swallowed 401 also lands here, and there the
  // login modal is the right outcome.
  if (!recordId) return null;
  const own = await getRoute(tkn, recordId).catch((err: any) => {
    // 404/403 mean the record is gone or not visible to this user, which
    // is a real 404 for this page. Anything else — a 500, an offline
    // browser — is a load failure, and painting "this Route does not
    // exist" over a transient backend error tells the user something
    // false. Let it through; the query settles it without a 404.
    const status = err?.response?.status;
    if (status === 404 || status === 403) return null;
    throw err;
  });
  if (!own) return null;
  return {
    ...conn,
    destinations: own.destinations ?? [],
    default_route: { id: own.id, name: own.name },
  };
};

// Loads the Route, whether you administer its workspace, and its provider
// integration, then keeps the page's editable copy of them in step with the
// server. An invalid subpage renders the 404 page, so nothing is fetched for it.
export const useRouteRecord = ({
  routeId,
  token,
  enabled,
}: {
  routeId: string | undefined;
  token: unknown;
  enabled: boolean;
}) => {
  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState(false);
  const [route, setRoute] = useState<Route>(buildRoute());
  // The Route's own (editable) name — decoupled from the provider Connection's
  // name, seeded from the route record whenever the Route is (re)fetched.
  const [routeName, setRouteName] = useState('');
  const [routeProvider, setRouteProvider] = useState<Provider>(buildProvider());
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [changesMade, setChangesMade] = useState(false);

  // Some Routes come back with default_route: null even though they have a
  // route in routing_rules (seen on stage, e.g. ad1e98d7…). Everything that
  // PATCHes the route (destinations list, field mappings) needs the route id,
  // so fall back to the Route's first routing rule.
  const defaultRouteId: string | undefined = routeRecordId(route);

  const isLoggedIn =
    !!keycloak?.authenticated &&
    localStorage.getItem('userAuthorized') !== null;
  const routeQuery = useQuery({
    queryKey: queryKeys.routes.detail(routeId ?? ''),
    // Not kept after leaving the page: a cached copy would fill the form on
    // the next visit and the refetch would then overwrite what the user
    // started typing. Saves still refresh it in place.
    gcTime: 0,
    enabled:
      enabled &&
      !!routeId &&
      isLoggedIn &&
      !!token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired(),
    queryFn: async () => {
      const tempRoute = await fetchRouteScoped(token as string, routeId ?? '');
      if (!tempRoute) return null;
      const [isAdmin, provider] = await Promise.all([
        loadCurrentUserDetails(tempRoute.owner.id, token as string),
        getIntegrationDetails(token as string, tempRoute.provider.id),
      ]);
      return {
        route: tempRoute,
        isAdmin: !!isAdmin,
        provider: provider as Provider,
      };
    },
  });

  // Reloading the Route after a save refetches it; the effects below copy
  // the result into the editable state.
  const requestRouteReload = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.routes.detail(routeId ?? ''),
      }),
    [queryClient, routeId],
  );

  useEffect(() => {
    const result = routeQuery.data;
    if (result === undefined) return;
    if (result === null) {
      // A swallowed 401 also resolves undefined. That is an expired
      // session (the login modal is already up), not a missing
      // Route — painting a 404 under it would strand the user on a
      // false negative after logging back in.
      if (!keycloak.isTokenExpired()) setNotFound(true);
      return;
    }
    setRoute(result.route);
    setCurrentMemberIsAdmin(result.isAdmin);
    // Reset only on success: an eager reset flashes the loading state over
    // the 404 page while a retry runs.
    setNotFound(false);
    setPageLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeQuery.data, routeQuery.dataUpdatedAt]);

  // The page's copy of the Route: name, provider and destinations. It waits
  // out a refresh in flight, so a save never briefly shows the pre-save
  // values. Destinations and the provider always follow the server — the
  // attach/remove and provider saves are built from them, and a stale copy
  // would send an old list or undo a change. Only the real drafts are held
  // back while unsaved: the name, the workspace picked for the provider and
  // the webhook settings being edited.
  useEffect(() => {
    const result = routeQuery.data;
    if (!result || routeQuery.isFetching) return;
    setDestinations(result.route.destinations);
    if (!changesMade) {
      setRouteName(routeDisplayName(result.route));
      setRouteProvider(result.provider);
      return;
    }
    setRouteProvider((draft: Provider) =>
      draft.id !== result.provider.id
        ? result.provider
        : {
            ...result.provider,
            owner: draft.owner,
            webhook_configuration: draft.webhook_configuration,
          },
    );
  }, [
    routeQuery.data,
    routeQuery.dataUpdatedAt,
    changesMade,
    routeQuery.isFetching,
  ]);

  useEffect(() => {
    if (!routeQuery.error) return;
    // 403 is treated like 404: the entity is not viewable by this user.
    // Anything else (500, offline) is not a 404; the page stays as it is.
    const status = (routeQuery.error as any)?.response?.status;
    if (status === 404 || status === 403) setNotFound(true);
    else console.log('Failed to load route details:', routeQuery.error);
  }, [routeQuery.error, routeQuery.errorUpdatedAt]);

  // The session token, or undefined once it has expired. Read it right before
  // each request: a token captured at render time can expire before then.
  const getToken = (): string | undefined =>
    token && typeof token === 'string' && !keycloak.isTokenExpired()
      ? token
      : undefined;

  return {
    routeId,
    getToken,
    route,
    routeQuery,
    defaultRouteId,
    pageLoaded,
    notFound,
    currentMemberIsAdmin,
    routeName,
    setRouteName,
    routeProvider,
    setRouteProvider,
    destinations,
    setDestinations,
    changesMade,
    setChangesMade,
    requestRouteReload,
  };
};
